import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Incident } from "./data/monitoring-service";

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

interface IncidentsTableProps {
  incidents: Incident[];
  renderAction?: (incident: Incident) => React.ReactNode;
  monitorColumnLabel?: string;
  taskStatusColumnLabel?: string;
  renderTaskStatus?: (incident: Incident) => React.ReactNode;
}

const formatEventDate = (date: string) => {
  const parsedApiDate = dayjs(date, "DD-MM-YYYY HH:mm", true);
  const parsedDate = parsedApiDate.isValid() ? parsedApiDate : dayjs(date);

  return parsedDate.isValid() ? parsedDate.format("MMM D, YYYY HH:mm") : date;
};

const IncidentsTable: React.FC<IncidentsTableProps> = ({
  incidents,
  renderAction,
  monitorColumnLabel = "Monitor",
  taskStatusColumnLabel = "Task status",
  renderTaskStatus,
}) => {
  const getSeverityIcon = (incident: Incident) => {
    if (incident.isResolved) {
      return <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 20 }} />;
    }
    if (incident.incidentType === "critical") {
      return <ErrorIcon sx={{ color: "#d32f2f", fontSize: 20 }} />;
    }
    return <WarningAmberIcon sx={{ color: "#ed6c02", fontSize: 20 }} />;
  };

  const getSeverityLabel = (incident: Incident) => {
    if (incident.isResolved) return "Resolved";
    if (incident.incidentType === "critical") return "Critical";
    return "Warning";
  };

  const getSeverityColor = (incident: Incident) => {
    if (incident.isResolved) return "#2e7d32";
    if (incident.incidentType === "critical") return "#d32f2f";
    return "#ed6c02";
  };

  const getActiveDuration = (
    firstDetected: string,
    resolved: string | null,
  ) => {
    const start = dayjs(firstDetected);
    const end = resolved ? dayjs(resolved) : dayjs();
    const days = end.diff(start, "days");
    return `${days}d`;
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        maxHeight: "100%",
        overflow: "auto",
      }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
              }}
            >
              Severity
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
              }}
            >
              {monitorColumnLabel}
            </TableCell>

            {renderTaskStatus && (
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: "grey.50",
                }}
              >
                {taskStatusColumnLabel}
              </TableCell>
            )}

            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
              }}
            >
              Problem
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
              }}
            >
              Active for
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
              }}
            >
              Last event
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                backgroundColor: "grey.50",
              }}
            >
              Status
            </TableCell>
            {renderAction && (
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: "grey.50",
                }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {incidents.map((incident) => (
            <TableRow
              key={incident.guid}
              sx={{
                "&:hover": {
                  backgroundColor: "grey.50",
                },
              }}
            >
              <TableCell>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  {getSeverityIcon(incident)}
                  <Typography
                    variant="caption"
                    sx={{
                      color: getSeverityColor(incident),
                      fontWeight: 600,
                      fontSize: "0.65rem",
                    }}
                  >
                    {getSeverityLabel(incident)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                >
                  {incident.monitorName}
                </Typography>
              </TableCell>
              {renderTaskStatus && (
                <TableCell>{renderTaskStatus(incident)}</TableCell>
              )}
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 0.5 }}
                >
                  {incident.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                >
                  {incident.description}
                </Typography>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#ed6c02",
                      fontSize: "0.875rem",
                    }}
                  >
                    {incident.runTime ||
                      getActiveDuration(
                        incident.firstDetectedDate,
                        incident.resolvedDate,
                      )}
                  </Typography>
                  {!incident.runTime && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                    >
                      Since{" "}
                      {dayjs(incident.firstDetectedDate).format("MMM D, YYYY")}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.875rem", mb: 0.5 }}
                  >
                    {formatEventDate(incident.publishedDate)} UTC
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: "0.7rem" }}
                  >
                    {incident.lastEventLabel || "Last failure"}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={incident.isResolved ? "Resolved" : "Active"}
                  size="small"
                  sx={{
                    backgroundColor: incident.isResolved
                      ? "#e8f5e9"
                      : "#fff4e5",
                    color: incident.isResolved ? "#2e7d32" : "#ed6c02",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    height: 24,
                  }}
                />
              </TableCell>
              {renderAction && <TableCell>{renderAction(incident)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default IncidentsTable;
